import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { TransferWallpaper } from '@wetransfer/transfer/adsdk/transfer-wallpaper-container';
import { useTransferMode } from '@wetransfer/transfer/component/TransferWindow/TransferWindow.hooks';
import { getTransferWallpaper } from '@wetransfer/transfer/customWallpaperApi';
import { normalizeCustomWallpaper } from '@wetransfer/transfer/customWallpaperApi/utils';
import {
  displayWallpaper,
  resetWallpaper,
} from '@wetransfer/transfer/helper/wallpaper';
import { useWallpaper } from '@wetransfer/transfer/modules/ad-sdk-wallpaper/hooks';
import {
  Wallpaper,
  CustomWallpapersAction,
  selectCustomWallpaper,
} from '@wetransfer/transfer/store/customWallpapers';
import { setCustomWallpaper } from '@wetransfer/transfer/store/customWallpapers/actions';
import {
  selectAccountTitle,
  selectAccountWallpaperLink,
} from '@wetransfer/transfer/store/selector/account';
import {
  selectTransferIdParam,
  selectTransferSecretParam,
} from '@wetransfer/transfer/store/selector/route';

import { TransferCustomizationContext } from './context';
import type { TransferCustomizationContextValue } from './context/types';
import { getWallpaperSrc } from './utils';

export const useTransferCustomization = (): TransferCustomizationContextValue =>
  useContext(TransferCustomizationContext);

/**
 * Given a `transferId`, load the custom wallpaper for the transfer (if any) and put it in the store
 */
export const useLoadCustomWallpaper = (transferId?: string): void => {
  const dispatch = useDispatch<Dispatch<CustomWallpapersAction>>();

  const setSelectedWallpaper = useCallback(
    (wallpaper: Wallpaper) => {
      dispatch(setCustomWallpaper(wallpaper));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!transferId) {
      return;
    }

    const getWallpaper = async () => {
      try {
        const response = await getTransferWallpaper(transferId);

        if (response?.wallpaperLargeUrl) {
          const wallpaper = normalizeCustomWallpaper(response);
          setSelectedWallpaper(wallpaper);
        }
      } catch {
        // Fail silently
      }
    };
    getWallpaper();
  }, [transferId, setSelectedWallpaper]);
};

/**
 * Given a `transferId`, load a custom wallpaper for the transfer (if any) and display it
 */
export const useDisplayCustomWallpaper = (): {
  getCustomWallpaper: () => TransferWallpaper;
} => {
  const transferId = useSelector(selectTransferIdParam);
  const transferSecret = useSelector(selectTransferSecretParam);

  const transferMode = useTransferMode({
    transferId,
    transferSecret,
  });
  useLoadCustomWallpaper(
    transferMode === 'download' && transferId && transferSecret
      ? transferId
      : undefined
  );
  const wallpaper = useSelector(selectCustomWallpaper);
  const title = useSelector(selectAccountTitle);
  const link = useSelector(selectAccountWallpaperLink);
  const { isInitialized } = useWallpaper();

  // TODO: deprecate the useEffect logic when adsdk 2.0 is 100% released
  // // Display wallpaper if everything is ready, and clean up afterwards
  useEffect(() => {
    if (isInitialized && wallpaper && transferId) {
      displayWallpaper(getWallpaperSrc(wallpaper), link, title);

      return resetWallpaper;
    }
  }, [isInitialized, wallpaper, link, title, transferId]);

  const getCustomWallpaper = useCallback(() => {
    if (wallpaper && transferId) {
      return {
        src: getWallpaperSrc(wallpaper),
        href: link,
        title,
        format: 'image',
      };
    }
  }, [link, title, transferId, wallpaper]);

  return {
    getCustomWallpaper,
  };
};
